import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const PICK = [["category", "Kategoria"], ["age_group", "Wiek"], ["school", "Szkoła"], ["instructor", "Instruktor"]];
const TEXT = [["time", "Godzina"]];
const NEW = "__new__";

const ComboField = ({ k, label, value, options, onChange, required, cls, wide }) => {
  const [custom, setCustom] = useState(false);
  return (
    <label className={`text-xs text-slate-400 ${wide ? "col-span-2" : ""}`}>{label}
      {custom ? (
        <div className="flex gap-1 mt-1">
          <input autoFocus data-testid={`perf-${k}-input`} required={required} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Wpisz nową wartość" className={cls} />
          <button type="button" data-testid={`perf-${k}-back-to-list`} onClick={() => { setCustom(false); onChange(""); }} className="h-10 px-2 rounded-lg border border-white/10 text-[11px] text-slate-300 shrink-0">Lista</button>
        </div>
      ) : (
        <select data-testid={`perf-${k}-select`} required={required} value={value}
          onChange={(e) => (e.target.value === NEW ? (setCustom(true), onChange("")) : onChange(e.target.value))} className={`${cls} mt-1`}>
          <option value="">— wybierz —</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value={NEW}>+ Dodaj nową…</option>
        </select>
      )}
    </label>
  );
};
const blank = { name: "", category: "", age_group: "", school: "", instructor: "", time: "", is_team: false, media: "" };

export const PerformanceDialog = ({ open, onClose, perf, day, days, onSaved }) => {
  const [f, setF] = useState(blank);
  const [touched, setTouched] = useState(false);
  const [opts, setOpts] = useState({ category: [], age_group: [], school: [], instructor: [] });
  useEffect(() => {
    if (!open) return;
    setF(perf ? { ...blank, ...perf } : { ...blank, day });
    setTouched(false);
    api.get("/admin/options").then((r) => setOpts(r.data));
  }, [open, perf, day]);

  const setCategory = (v) => {
    const auto = /\b(DUO|DUET|GROUP|FORMACJA|FORMACJE|MINIFORMACJA)\b/i.test(v);
    setF((x) => ({ ...x, category: v, is_team: !perf && !touched ? auto : x.is_team }));
  };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, category: f.category.toUpperCase().trim(), order: perf ? perf.order : null };
    try {
      if (perf) await api.put(`/admin/performances/${perf.id}`, body);
      else await api.post("/admin/performances", body);
      toast.success("Zapisano");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const input = "w-full h-10 px-3 rounded-lg bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none text-sm";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#1C2541] border-blue-500/30 w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="perf-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{perf ? "Edytuj występ" : "Dodaj występ"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-xs text-slate-400">Dzień
            <select data-testid="perf-day-select" value={f.day} onChange={(e) => setF({ ...f, day: e.target.value })} className={`${input} mt-1`}>
              {days.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label className="col-span-2 text-xs text-slate-400">Imię i nazwisko / nazwa grupy
            <input data-testid="perf-name-input" required value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${input} mt-1`} />
          </label>
          {PICK.map(([k, l]) => (
            <ComboField key={k} k={k} label={l} value={f[k] || ""} options={opts[k] || []} cls={input} wide={k === "category"} required={k === "category"}
              onChange={(v) => (k === "category" ? setCategory(v) : setF((x) => ({ ...x, [k]: v })))} />
          ))}
          {TEXT.map(([k, l]) => (
            <label key={k} className="text-xs text-slate-400">{l}
              <input data-testid={`perf-${k}-input`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${input} mt-1`} />
            </label>
          ))}
          <label className="col-span-2 text-xs text-slate-400">Multimedia (dla techniki)
            <select data-testid="perf-media-select" value={f.media || ""} onChange={(e) => setF({ ...f, media: e.target.value })} className={`${input} mt-1`}>
              <option value="">— brak informacji —</option>
              <option value="music">Muzyka</option>
              <option value="music_photo">Muzyka + zdjęcie</option>
              <option value="video">Wideo (ekran z muzyką)</option>
            </select>
          </label>
          <label className="col-span-2 flex items-center gap-3 text-sm mt-1">
            <Switch data-testid="perf-team-switch" checked={!!f.is_team} onCheckedChange={(v) => { setTouched(true); setF({ ...f, is_team: v }); }} />
            Duet / grupa (ocena synchronizacji w technice)
          </label>
          <button data-testid="perf-save-btn" className="col-span-2 h-11 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold mt-2">Zapisz</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
